import React, { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";

const cardStyle = { padding: 12, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 8 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 };
const btnStyle = { padding: "8px 14px", fontSize: 12, fontWeight: 700, background: "var(--pf-ember)", color: "#fff", border: 0, borderRadius: 5, cursor: "pointer" };

export default function PipeSupportsPanel({ projectId }) {
  const [types, setTypes] = useState([]);
  const [supports, setSupports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    if (!projectId) return;
    try {
      const [t, s] = await Promise.all([
        api.get(`/projects/${projectId}/pipe-supports/types`),
        api.get(`/projects/${projectId}/pipe-supports`),
      ]);
      setTypes(t.data); setSupports(s.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { load(); }, [projectId]);

  const autoPlace = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const sample_lines = [{
        id: "L-100-001-1A1", size_in: 6, design_T_C: 250,
        "3d_routing": { waypoints: [
          { x: 0, y: 0, z: 1.5 }, { x: 15, y: 0, z: 1.5 },
          { x: 15, y: 8, z: 1.5 }, { x: 15, y: 8, z: 6 },
          { x: 15, y: 8, z: 6 + 0.01 }, { x: 25, y: 8, z: 6 },
        ] },
      }];
      const { data } = await api.post(
        `/projects/${projectId}/pipe-supports/auto-place`,
        { lines: sample_lines, overwrite: true },
      );
      setMsg(`Placed ${data.placed_count} supports (${Object.entries(data.by_type).filter(([_, v]) => v).map(([k, v]) => `${k}:${v}`).join(", ")}). [Sample 6″ hot line.]`);
      await load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  const downloadManifest = () => {
    window.open(`${api.defaults.baseURL}/projects/${projectId}/pipe-supports/manifest.json`, "_blank");
  };

  const byType = {};
  for (const s of supports) byType[s.type] = (byType[s.type] || 0) + 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {err && <div style={{ padding: 8, background: "rgba(239,68,68,.1)", color: "#FF8888", borderRadius: 5, fontSize: 12 }}>{err}</div>}
      {msg && <div style={{ padding: 8, background: "rgba(34,197,94,.1)", color: "#22c55e", borderRadius: 5, fontSize: 12 }}>{msg}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
        <KPI label="Total Supports" value={supports.length} />
        {types.slice(0, 5).map((t) => <KPI key={t.code} label={`${t.name}`} value={byType[t.code] || 0} />)}
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Auto-Placement Engine — ASME B31.3 Table 321.1</div>
          <button data-testid="psup-auto-place" style={btnStyle} onClick={autoPlace} disabled={busy}>
            {busy ? "Placing…" : "Run Auto-Placement (Sample Line)"}
          </button>
          <button data-testid="psup-manifest" onClick={downloadManifest} style={{ ...btnStyle, background: "var(--pf-surface-2)", color: "var(--pf-fg)", border: "1px solid var(--pf-border)" }}>
            Download manifest.json
          </button>
        </div>
        <div style={{ fontSize: 11, color: "var(--pf-muted)", marginTop: 6 }}>
          Calculates max-span (size + temperature creep + density), inserts shoes/guides on straight runs, anchors at every direction change ≥ 30°, spring hangers on hot lines &gt; 200 °C, trunnions on vertical risers. Pass real PDO lines from your run via the API or CLI.
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ ...labelStyle, marginBottom: 8 }}>Support Type Catalogue</div>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--pf-surface-2)" }}>
              <th style={th}>Code</th><th style={th}>Name</th><th style={th}>Use</th><th style={th}>Count</th>
            </tr>
          </thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.code} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                <td style={td}><strong style={{ color: "#F26B1F" }}>{t.code}</strong></td>
                <td style={td}>{t.name}</td>
                <td style={{ ...td, color: "var(--pf-muted)" }}>{t.use}</td>
                <td style={td}>{byType[t.code] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {supports.length > 0 && (
        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Placed Supports — {supports.length} entries</div>
          <div style={{ maxHeight: 320, overflow: "auto" }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--pf-surface-2)", position: "sticky", top: 0 }}>
                  {["Tag", "Type", "Line", "X", "Y", "Z (m)", "Size″", "T °C", "Load kN", "Note"].map((h) =>
                    <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {supports.map((s) => (
                  <tr key={s.id} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                    <td style={td}><strong>{s.tag}</strong></td>
                    <td style={td}>{s.type_name}</td>
                    <td style={td}>{s.line}</td>
                    <td style={td}>{s.x_m}</td>
                    <td style={td}>{s.y_m}</td>
                    <td style={td}>{s.z_m}</td>
                    <td style={td}>{s.pipe_size_inch}</td>
                    <td style={td}>{s.design_T_C}</td>
                    <td style={td}>{s.load_kN}</td>
                    <td style={{ ...td, color: "var(--pf-muted)" }}>{s.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 };
const td = { padding: 6, fontFamily: "monospace" };

function KPI({ label, value }) {
  return (
    <div style={{ padding: 10, background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", borderRadius: 6, borderLeft: "3px solid #F26B1F" }}>
      <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}
